// @tier: PREMIUM — Composeur d'annonce admin → parents ou chauffeurs (Vue pure, logique dans hooks/trBroadcast.js)
import { ArrowLeft, Send, Users, AlertTriangle, Loader2, Languages, Bell } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import useBroadcast, { AUDIENCE_MODES, RECIPIENT_TYPES, recipientNoun } from '../hooks/trBroadcast';
import { FORM_LIMITS } from '../config/constants';
import { PRIORITY_CONFIG, PRIORITY_VALUES, getPriorityConfig } from '../utils/notificationPriority';

const RECIPIENT_LABELS = { [RECIPIENT_TYPES.PARENT]: 'Parents', [RECIPIENT_TYPES.DRIVER]: 'Chauffeurs' };

// Libellés des modes d'audience, par type de destinataire.
const AUDIENCE_LABELS = {
  [RECIPIENT_TYPES.PARENT]: {
    [AUDIENCE_MODES.SCHOOL]: { title: "Toute l'école", hint: 'Tous les parents' },
    [AUDIENCE_MODES.ROUTES]: { title: 'Circuits', hint: 'Les parents des circuits choisis' },
    [AUDIENCE_MODES.STOPS]: { title: 'Arrêts', hint: 'Les parents des arrêts choisis' },
    [AUDIENCE_MODES.CUSTOM]: { title: 'Personnalisé', hint: 'Choisir les parents un par un' },
  },
  [RECIPIENT_TYPES.DRIVER]: {
    [AUDIENCE_MODES.SCHOOL]: { title: 'Tous les chauffeurs', hint: 'Tous les chauffeurs actifs' },
    [AUDIENCE_MODES.ROUTES]: { title: 'Circuits', hint: 'Les chauffeurs des circuits choisis' },
    [AUDIENCE_MODES.CUSTOM]: { title: 'Personnalisé', hint: 'Choisir les chauffeurs un par un' },
  },
};

const SEARCH_PLACEHOLDERS = {
  [AUDIENCE_MODES.ROUTES]: 'Rechercher un circuit…',
  [AUDIENCE_MODES.STOPS]: 'Rechercher un arrêt…',
  [`${AUDIENCE_MODES.CUSTOM}-${RECIPIENT_TYPES.PARENT}`]: 'Rechercher un parent…',
  [`${AUDIENCE_MODES.CUSTOM}-${RECIPIENT_TYPES.DRIVER}`]: 'Rechercher un chauffeur…',
};

const LANGUAGE_LABELS = { fr: 'Français', en: 'English', ar: 'العربية' };

const CHECKBOX_CLASS = 'h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500';

const fieldError = (message) => (message ? <p className="text-sm text-red-600 mt-1">{message}</p> : null);

const Counter = ({ value, max, soft }) => (
  <span
    className={`text-xs ${value > max ? 'text-red-600' : soft && value > soft ? 'text-orange-600' : 'text-gray-500'}`}
  >
    {value} / {max}
  </span>
);

const BroadcastComposer = () => {
  const {
    recipientType,
    changeRecipientType,
    availableModes,
    audienceMode,
    changeAudienceMode,
    selectedIds,
    toggleAudienceId,
    selectAllVisible,
    clearSelection,
    audienceSearch,
    setAudienceSearch,
    audienceOptions,
    audienceLoading,
    form,
    updateField,
    showTranslations,
    setShowTranslations,
    languages,
    previewLanguage,
    setPreviewLanguage,
    errors,
    preview,
    previewingNow,
    previewFailed,
    noRecipient,
    strongConfirmation,
    confirmOpen,
    setConfirmOpen,
    confirmChecked,
    setConfirmChecked,
    sending,
    requestSend,
    confirmSend,
    cancel,
  } = useBroadcast();

  const audienceReady = audienceMode === AUDIENCE_MODES.SCHOOL || selectedIds.length > 0;
  const sendDisabled = sending || !audienceReady || previewingNow || noRecipient;
  const priority = getPriorityConfig(form.priority);
  const labels = AUDIENCE_LABELS[recipientType];
  const total = preview?.total ?? 0;
  const reachable = preview?.reachable ?? 0;

  const previewTexts = {
    fr: { title: form.title, message: form.message },
    en: { title: form.titleEn, message: form.messageEn },
    ar: { title: form.titleAr, message: form.messageAr },
  }[previewLanguage];

  return (
    <div className="space-y-6 pb-24 md:pb-0">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Nouvelle annonce</h1>
          <p className="text-gray-600 mt-1">Envoyez un message à des parents ou à des chauffeurs</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={cancel}>
            <ArrowLeft className="w-4 h-4" />
            Retour aux notifications
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          {/* Destinataires */}
          <section className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Destinataires
            </h2>

            {/* Parents / Chauffeurs : le titre et le message saisis sont conservés quand on bascule */}
            <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Type de destinataire">
              {Object.values(RECIPIENT_TYPES).map((type) => (
                <button
                  key={type}
                  type="button"
                  role="radio"
                  aria-checked={recipientType === type}
                  onClick={() => changeRecipientType(type)}
                  className={`p-3 border-2 rounded-lg font-medium transition-colors ${
                    recipientType === type
                      ? 'border-blue-600 bg-blue-50 text-blue-900'
                      : 'border-gray-200 text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {RECIPIENT_LABELS[type]}
                </button>
              ))}
            </div>

            <div
              className={`grid grid-cols-1 gap-2 ${availableModes.length === 4 ? 'sm:grid-cols-4' : 'sm:grid-cols-3'}`}
              role="radiogroup"
              aria-label="Audience"
            >
              {availableModes.map((mode) => (
                <button
                  key={mode}
                  type="button"
                  role="radio"
                  aria-checked={audienceMode === mode}
                  onClick={() => changeAudienceMode(mode)}
                  className={`p-3 border-2 rounded-lg text-left transition-colors ${
                    audienceMode === mode ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="block font-medium text-gray-900">{labels[mode].title}</span>
                  <span className="block text-xs text-gray-600">{labels[mode].hint}</span>
                </button>
              ))}
            </div>

            {audienceMode !== AUDIENCE_MODES.SCHOOL && (
              <div className="space-y-2">
                <Input
                  value={audienceSearch}
                  onChange={(event) => setAudienceSearch(event.target.value)}
                  placeholder={
                    SEARCH_PLACEHOLDERS[
                      audienceMode === AUDIENCE_MODES.CUSTOM ? `${audienceMode}-${recipientType}` : audienceMode
                    ]
                  }
                  aria-label="Rechercher"
                />
                {audienceMode === AUDIENCE_MODES.CUSTOM && (
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <Button type="button" variant="outline" size="sm" onClick={selectAllVisible}>
                      Tout sélectionner
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={clearSelection}>
                      Tout désélectionner
                    </Button>
                    <span className="text-gray-600">
                      {selectedIds.length} {recipientNoun(recipientType, selectedIds.length)} sélectionné
                      {selectedIds.length > 1 ? 's' : ''}
                    </span>
                  </div>
                )}
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-64 overflow-y-auto">
                  {audienceLoading ? (
                    <p className="p-4 text-sm text-gray-500">Chargement…</p>
                  ) : audienceOptions.length === 0 ? (
                    <p className="p-4 text-sm text-gray-500">Aucun résultat</p>
                  ) : (
                    audienceOptions.map((option) => (
                      <label key={option.id} className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          className={CHECKBOX_CLASS}
                          checked={selectedIds.includes(option.id)}
                          onChange={() => toggleAudienceId(option.id)}
                          aria-label={option.name}
                        />
                        <span className="text-sm text-gray-900">{option.name}</span>
                      </label>
                    ))
                  )}
                </div>
                {!audienceReady && <p className="text-sm text-gray-600">Sélectionnez au moins un élément.</p>}
              </div>
            )}

            <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 text-sm" aria-live="polite">
              {!audienceReady ? (
                <span className="text-gray-600">
                  Choisissez des destinataires pour voir combien de {recipientNoun(recipientType)} sont touchés.
                </span>
              ) : previewFailed ? (
                <span className="text-red-600">
                  Impossible de calculer le nombre de {recipientNoun(recipientType)}.
                </span>
              ) : previewingNow && !preview ? (
                <span className="text-gray-600 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Calcul en cours…
                </span>
              ) : noRecipient ? (
                <span className="text-red-600">
                  Aucun {recipientNoun(recipientType, 1)} ne correspond à cette audience.
                </span>
              ) : (
                <span className="text-gray-900">
                  <strong>{total}</strong> {recipientNoun(recipientType, total)}, dont <strong>{reachable}</strong>{' '}
                  joignable{reachable > 1 ? 's' : ''}
                </span>
              )}
            </div>
          </section>

          {/* Contenu */}
          <section className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Message</h2>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="broadcast-title" className="text-sm font-medium text-gray-700">
                  Titre
                </label>
                <Counter
                  value={form.title.length}
                  max={FORM_LIMITS.BROADCAST_TITLE_MAX}
                  soft={FORM_LIMITS.BROADCAST_TITLE_SOFT_LIMIT}
                />
              </div>
              <Input
                id="broadcast-title"
                value={form.title}
                onChange={(event) => updateField('title', event.target.value)}
                aria-invalid={Boolean(errors.title)}
              />
              {form.title.length > FORM_LIMITS.BROADCAST_TITLE_SOFT_LIMIT && (
                <p className="text-xs text-orange-600 mt-1">
                  Un titre long risque d&apos;être tronqué sur l&apos;écran verrouillé.
                </p>
              )}
              {fieldError(errors.title)}
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="broadcast-message" className="text-sm font-medium text-gray-700">
                  Message
                </label>
                <Counter value={form.message.length} max={FORM_LIMITS.BROADCAST_BODY_MAX} />
              </div>
              <Textarea
                id="broadcast-message"
                rows={5}
                value={form.message}
                onChange={(event) => updateField('message', event.target.value)}
                aria-invalid={Boolean(errors.message)}
              />
              {fieldError(errors.message)}
            </div>

            {!showTranslations ? (
              <Button type="button" variant="outline" size="sm" onClick={() => setShowTranslations(true)}>
                <Languages className="w-4 h-4" />
                Ajouter des traductions
              </Button>
            ) : (
              <div className="space-y-4 border-t border-gray-100 pt-4">
                <p className="text-sm text-gray-600">
                  Les parents dont l&apos;application est en anglais ou en arabe reçoivent leur version. Sans
                  traduction, ils reçoivent le français.
                </p>
                {[
                  { suffix: 'En', label: 'English', dir: 'ltr' },
                  { suffix: 'Ar', label: 'العربية', dir: 'rtl' },
                ].map(({ suffix, label, dir }) => (
                  <fieldset key={suffix} className="space-y-2">
                    <legend className="text-sm font-medium text-gray-700 mb-1">{label}</legend>
                    <Input
                      dir={dir}
                      value={form[`title${suffix}`]}
                      onChange={(event) => updateField(`title${suffix}`, event.target.value)}
                      placeholder="Titre"
                      aria-label={`Titre (${label})`}
                    />
                    <Textarea
                      dir={dir}
                      rows={3}
                      value={form[`message${suffix}`]}
                      onChange={(event) => updateField(`message${suffix}`, event.target.value)}
                      placeholder="Message"
                      aria-label={`Message (${label})`}
                    />
                    {fieldError(errors[`translation${suffix}`])}
                  </fieldset>
                ))}
              </div>
            )}
          </section>

          {/* Priorité */}
          <section className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">Priorité</h2>
            <select
              value={form.priority}
              onChange={(event) => updateField('priority', event.target.value)}
              aria-label="Priorité"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            >
              {PRIORITY_VALUES.map((value) => (
                <option key={value} value={value}>
                  {PRIORITY_CONFIG[value].label}
                </option>
              ))}
            </select>
            {form.priority === 'urgent' && (
              <div
                className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800"
                role="alert"
              >
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                Ignore les préférences de notification des parents.
              </div>
            )}
          </section>
        </div>

        {/* Aperçu */}
        <div>
          <section className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 space-y-4 lg:sticky lg:top-6">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-lg font-semibold text-gray-900">Aperçu</h2>
              {languages.length > 1 && (
                <div className="flex gap-1" role="group" aria-label="Langue de l'aperçu">
                  {languages.map((language) => (
                    <Button
                      key={language}
                      type="button"
                      size="sm"
                      variant={previewLanguage === language ? 'default' : 'outline'}
                      onClick={() => setPreviewLanguage(language)}
                    >
                      {LANGUAGE_LABELS[language]}
                    </Button>
                  ))}
                </div>
              )}
            </div>
            <div
              className="rounded-2xl border border-gray-200 bg-gray-50 p-4 flex gap-3"
              dir={previewLanguage === 'ar' ? 'rtl' : 'ltr'}
              data-testid="notification-preview"
            >
              <div className="w-10 h-10 shrink-0 rounded-xl bg-blue-600 text-white flex items-center justify-center">
                <Bell className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-gray-900 break-words">{previewTexts.title || 'Titre de l’annonce'}</p>
                <p className="text-sm text-gray-700 break-words whitespace-pre-line">
                  {previewTexts.message || 'Le message apparaîtra ici.'}
                </p>
                <span
                  className={`inline-block mt-2 px-2 py-0.5 rounded-full text-xs font-medium ${priority.bg} ${priority.text}`}
                >
                  {priority.label}
                </span>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Barre d'envoi : fixe en bas sur mobile */}
      <div className="fixed bottom-0 inset-x-0 z-10 bg-white border-t border-gray-200 p-3 flex justify-end gap-2 md:static md:z-auto md:border-0 md:bg-transparent md:p-0">
        <Button variant="outline" onClick={cancel} disabled={sending}>
          Annuler
        </Button>
        <Button onClick={requestSend} disabled={sendDisabled}>
          <Send className="w-4 h-4" />
          Envoyer l&apos;annonce
        </Button>
      </div>

      <Dialog open={confirmOpen} onOpenChange={(open) => !sending && setConfirmOpen(open)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Envoyer l&apos;annonce ?</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2">
                <p>
                  Elle sera envoyée à <strong>{total}</strong> {recipientNoun(recipientType, total)} (dont{' '}
                  <strong>{reachable}</strong> joignable{reachable > 1 ? 's' : ''}).
                </p>
                <p>
                  Destinataires : {RECIPIENT_LABELS[recipientType].toLowerCase()} ·{' '}
                  {labels[audienceMode].title.toLowerCase()}
                  {audienceMode !== AUDIENCE_MODES.SCHOOL && ` (${selectedIds.length})`} · Priorité :{' '}
                  {priority.label.toLowerCase()}
                </p>
                {form.priority === 'urgent' && (
                  <p className="text-red-700">Cette annonce ignore les préférences de notification.</p>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          {strongConfirmation && (
            <label className="flex items-start gap-3 text-sm text-gray-900">
              <input
                type="checkbox"
                className={`${CHECKBOX_CLASS} mt-0.5`}
                checked={confirmChecked}
                onChange={(event) => setConfirmChecked(event.target.checked)}
                aria-label="Confirmation d'envoi"
              />
              <span>
                Je comprends que cette annonce sera envoyée à {total} {recipientNoun(recipientType, total)}.
              </span>
            </label>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={sending}>
              Annuler
            </Button>
            <Button onClick={confirmSend} disabled={sending || (strongConfirmation && !confirmChecked)}>
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BroadcastComposer;
